/**
 * Typed client for the Operation Hub backend's Array credit-pull wrappers.
 *
 * The SPA never calls Array directly. All three functions hit the wrapper
 * routes at `${REACT_APP_ARRAY_API_BASE_URL}/{order,status/:orderId,file/:reportId}`.
 *
 * In deployed envs that base URL points at the Operation Hub backend's
 * `/credit/array` routes (which hold `x-api-key` and proxy to Array server-side).
 * In local dev the default base `/credit/array` is intercepted by setupProxy.js
 * and forwarded to the stg API Gateway with `x-api-key` injected there — same
 * contract, no Keycloak dependency.
 *
 * Every call generates (or forwards) a correlation ID so a single credit-pull
 * flow can be reconstructed across Operation Hub logs and the Array
 * `audit_requests` table.
 */

import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { generateCorrelationId } from '../../utils/correlationId';
import { keycloakService } from '../keycloakService';
import {
  ArrayError,
  ArrayErrorCode,
  FetchArrayFileResponse,
  OrderArrayReportRequest,
  OrderArrayReportResponse,
  RetrieveArrayReportResponse,
  ArrayReportType,
} from './types';

// Resolution order:
//   1. REACT_APP_ARRAY_API_URL          — set per-env from terraform output l3_function_url
//   2. REACT_APP_ARRAY_API_BASE_URL     — legacy override (kept for compatibility)
//   3. /credit/array                    — same-origin, intercepted by setupProxy.js in local dev
const arrayClient = axios.create({
  baseURL:
    process.env.REACT_APP_ARRAY_API_URL ||
    process.env.REACT_APP_ARRAY_API_BASE_URL ||
    '/credit/array',
  timeout: 30000,
});

// Attach Keycloak Bearer on every request when going to the deployed L3
// proxy (which validates the token in the handler). When falling back to
// `/credit/array` (local setupProxy.js path), the Authorization header is
// harmless — the dev-server proxy authenticates via SSO IAM creds, not JWT.
arrayClient.interceptors.request.use((config) => {
  const token = keycloakService.getToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

interface CallOptions {
  correlationId?: string;
}

function headers(correlationId: string): AxiosRequestConfig['headers'] {
  return { 'x-correlation-id': correlationId };
}

function mapErrorCode(status: number | undefined, body: unknown): ArrayErrorCode {
  const message = typeof body === 'object' && body !== null && 'message' in body
    ? String((body as { message?: unknown }).message ?? '')
    : '';
  if (status === 400) return 'validation';
  if (status === 401 || status === 403) return 'auth_misconfigured';
  if (status === 404) {
    if (/not available yet/i.test(message)) return 'report_not_ready';
    return 'unknown_order';
  }
  if (status === 422) {
    if (/not verified/i.test(message)) return 'unverified_user';
    if (/expired/i.test(message)) return 'report_expired';
  }
  if (status === 500 && /failed to save/i.test(message)) return 'saved_after_order';
  return 'unknown';
}

function toArrayError(err: unknown, correlationId: string): ArrayError {
  const axiosErr = err as AxiosError<unknown>;
  const status = axiosErr?.response?.status;
  const body = axiosErr?.response?.data;
  const code = mapErrorCode(status, body);
  const message = typeof body === 'object' && body !== null && 'message' in body
    ? String((body as { message?: unknown }).message ?? `Array request failed (${status ?? 'network'})`)
    : `Array request failed (${status ?? 'network'})`;
  const e: ArrayError = Object.assign(new Error(message), {
    code,
    status,
    correlationId,
    raw: body,
    name: 'ArrayError',
  });
  return e;
}

export async function orderReport(
  request: OrderArrayReportRequest,
  options: CallOptions = {},
): Promise<{ data: OrderArrayReportResponse; correlationId: string }> {
  const correlationId = options.correlationId ?? generateCorrelationId();
  const { offCycle, arrayClientKey, ...rest } = request;
  try {
    const response = await arrayClient.post<OrderArrayReportResponse>(
      '/order',
      // Body: arrayClientKey only (normalized to lowercase). offCycle is a
      // QUERY parameter per the Array order-report Lambda contract; sending
      // it in the body is a silent no-op (falls back to cache-hit every time).
      { ...rest, arrayClientKey: arrayClientKey.toLowerCase() },
      {
        headers: headers(correlationId),
        params: offCycle ? { offCycle: 'true' } : undefined,
      },
    );
    return { data: response.data, correlationId };
  } catch (err) {
    throw toArrayError(err, correlationId);
  }
}

export async function retrieveReport(
  orderId: string,
  options: CallOptions = {},
): Promise<{ data: RetrieveArrayReportResponse; correlationId: string }> {
  const correlationId = options.correlationId ?? generateCorrelationId();
  try {
    const response = await arrayClient.get<RetrieveArrayReportResponse>(
      `/status/${encodeURIComponent(orderId)}`,
      { headers: headers(correlationId) },
    );
    return { data: response.data, correlationId };
  } catch (err) {
    throw toArrayError(err, correlationId);
  }
}

export async function fetchFile(
  reportId: string,
  reportType: ArrayReportType,
  options: CallOptions = {},
): Promise<{ data: FetchArrayFileResponse; correlationId: string }> {
  const correlationId = options.correlationId ?? generateCorrelationId();
  try {
    const response = await arrayClient.get<FetchArrayFileResponse>(
      `/file/${encodeURIComponent(reportId)}`,
      { params: { reportType }, headers: headers(correlationId) },
    );
    return { data: response.data, correlationId };
  } catch (err) {
    throw toArrayError(err, correlationId);
  }
}
